import { phone as parsePhone } from "phone";

import { FIELD_TYPES } from "./field-types.ts";

/** A form control's value paired with the value stored on the entity. The two
 *  differ for phone (the control splits country code from the national number)
 *  and for select/member (the control wants option objects, storage wants ids). */
export type FieldValuePair = { formValue: unknown; fieldValue: unknown };

type PhoneParts = { countryCode?: unknown; value?: unknown };

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null;

/** Storage value to control value. */
export function dataToField(type: string, value: unknown): FieldValuePair {
  switch (type) {
    case FIELD_TYPES.phone:
      if (value && typeof value === "string") {
        // Lenient on purpose: mobile-prefix checking rejects real landlines,
        // and a stored value that no longer parses travels through raw rather
        // than being dropped.
        const parsed = parsePhone(value, { validateMobilePrefix: false });
        if (!parsed.isValid) {
          return { formValue: value, fieldValue: value };
        }
        const national = parsed.phoneNumber.slice(parsed.countryCode.length);
        return {
          formValue: {
            countryCode: parsed.countryCode,
            value: national,
          },
          fieldValue: {
            countryCode: parsed.countryIso2,
            value: national,
            country: parsed.countryIso2,
          },
        };
      }
      return { formValue: value, fieldValue: value };
    case FIELD_TYPES.url:
      return { formValue: `${value || ""}`, fieldValue: `${value || ""}` };
    case FIELD_TYPES.number:
      return {
        formValue: value == null ? "" : `${value}`,
        fieldValue: value == null ? "" : `${value}`,
      };
    case FIELD_TYPES.money:
      return {
        formValue: value == null ? "" : `${value}`,
        fieldValue: `${value || ""}`,
      };
    case FIELD_TYPES.select:
      return {
        formValue:
          (value as unknown[] | null | undefined)?.map((option) => ({
            value: option,
            label: option,
            selected: true,
          })) ?? [],
        fieldValue: value,
      };
    case FIELD_TYPES.member:
      return {
        formValue:
          (value as unknown[] | null | undefined)?.map((member) => ({
            value: member,
            selected: true,
          })) ?? [],
        fieldValue: value,
      };
    case FIELD_TYPES.checkbox:
      return {
        formValue: value == null ? null : !!value,
        fieldValue: value == null ? null : !!value,
      };
  }
  return { formValue: value, fieldValue: value };
}

/** Control value to storage value.
 *
 *  `strict` decides what happens to input that cannot be parsed: it becomes
 *  `null` instead of being persisted as `NaN` or a bad date. */
export function fieldDto(
  type: string,
  value: unknown,
  options: { strict?: boolean } = {},
): unknown {
  const { strict = false } = options;
  let out: unknown = value;

  switch (type) {
    case FIELD_TYPES.money:
      if (out != null) {
        if (typeof out === "number") break;
        if (typeof out === "string") {
          const parsed = parseFloat(out.replace(/[^\d.-]/g, ""));
          out = strict && isNaN(parsed) ? null : parsed;
        }
      }
      break;
    case FIELD_TYPES.phone:
      if (out) {
        if (typeof out === "string") break;
        if (isRecord(out)) {
          const parts = out as PhoneParts;
          if (parts.countryCode && parts.value) {
            // Persist E.164 when the pair parses; an invalid pair persists as
            // the raw concatenation instead of throwing out of the DTO — the
            // validation gate is where a bad number gets rejected.
            const combined = `${parts.countryCode}${parts.value}`;
            const parsed = parsePhone(combined, { validateMobilePrefix: false });
            out = parsed.isValid ? parsed.phoneNumber : combined;
          }
        }
      }
      break;
    case FIELD_TYPES.date:
    case FIELD_TYPES.dateTime:
      if (out) {
        if (out instanceof Date) break;
        const dateValue = new Date(out as string | number);
        if (!isNaN(dateValue.getTime())) {
          out = dateValue;
        } else if (strict) {
          out = null;
        }
      }
      break;
    case FIELD_TYPES.time:
      if (out) {
        if (out instanceof Date) break;
        if (typeof out === "string") {
          // Already an ISO date string
          if (/^\d{4}-\d{2}-\d{2}T/.test(out)) {
            out = new Date(out);
            break;
          }
          // 24-hour format ("14:30" or "14:30:00"). The seconds group is the
          // only optional one, so it is the only default that a real input
          // reaches; the others stand in for groups the match guarantees.
          const time24 = out.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
          if (time24) {
            const [, rawHours = "0", rawMinutes = "0", rawSeconds = "0"] = time24;
            // Epoch date as the baseline — only the time-of-day is meaningful.
            out = new Date(
              Date.UTC(1970, 0, 1, parseInt(rawHours, 10), parseInt(rawMinutes, 10), parseInt(rawSeconds, 10)),
            );
            break;
          }
          // 12-hour format ("02:30 PM" or "2:30pm")
          const time12 = out.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([ap]m)$/i);
          if (time12) {
            const [, rawHours = "0", rawMinutes = "0", rawSeconds = "0", meridiem = "am"] = time12;
            let hours = parseInt(rawHours, 10);
            const isPM = meridiem.toLowerCase() === "pm";
            if (isPM && hours !== 12) hours += 12;
            if (!isPM && hours === 12) hours = 0;
            out = new Date(
              Date.UTC(1970, 0, 1, hours, parseInt(rawMinutes, 10), parseInt(rawSeconds, 10)),
            );
            break;
          }
          if (strict) {
            out = null;
          }
        }
      }
      break;
    case FIELD_TYPES.number:
      if (out != null) {
        if (typeof out === "number") break;
        if (typeof out === "string") {
          out = parseFloat(out.replace(/[^\d.-]/g, ""));
        }
      } else {
        out = null;
      }
      break;
    case FIELD_TYPES.address:
      if (!out) {
        out = {};
      }
      break;
    case FIELD_TYPES.member: {
      // Always an array of id strings — single-mode caps selection at 1 in the
      // UI but persists the same array shape.
      const list = Array.isArray(out) ? out : out ? [out] : [];
      out = list.reduce<string[]>((members, v) => {
        const memberId = isRecord(v) ? v.value : v;
        if (memberId) {
          members.push(memberId as string);
        }
        return members;
      }, []);
      break;
    }
    case FIELD_TYPES.checkbox:
      if (out != null) {
        out = !!out;
      }
      break;
    case FIELD_TYPES.select: {
      // Always an array of option values — single-mode caps selection at 1 in
      // the UI but persists the same array shape.
      const list = Array.isArray(out) ? out : out ? [out] : [];
      out = list.map((v) => (isRecord(v) ? v.value : v));
      break;
    }
  }

  return out;
}
